import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@shared/api/client';
import { getDocumentDownloadUrl } from '@shared/api/documents';
import type { DocumentResponse, Uuid } from '@shared/types/api';
import { Dialog } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

// Lists every document on a transaction. Mount inside the Transactions table
// when the Associate clicks "View Documents".
export function DocumentsDialog({
  open,
  onClose,
  transactionId,
}: {
  open: boolean;
  onClose: () => void;
  transactionId: Uuid;
}) {
  const { data, isLoading } = useQuery({
    queryKey: ['transaction', transactionId, 'documents'],
    queryFn: async () => {
      const { data } = await api.get<{ items: DocumentResponse[] }>(
        `/transactions/${transactionId}/documents`,
      );
      return data.items;
    },
    enabled: open,
    staleTime: 10_000,
  });

  const items = data ?? [];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Documents"
      footer={
        <Button variant="ghost" onClick={onClose}>
          Close
        </Button>
      }
      className="max-w-xl"
    >
      {isLoading ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : items.length === 0 ? (
        <p className="text-sm text-gray-500">No documents uploaded yet.</p>
      ) : (
        <ul className="divide-y divide-gray-100">
          {items.map((d) => (
            <li key={d.id} className="flex items-center justify-between gap-3 py-2">
              <div className="min-w-0">
                <div className="truncate text-sm font-medium text-gray-900">{d.fileName}</div>
                <div className="text-xs text-gray-500">
                  {d.kind}
                  {d.draftVersion ? ` · v${d.draftVersion}` : ''}
                  {d.archivedAt ? ' · archived' : ''} · {(Number(d.sizeBytes) / 1024).toFixed(0)} KB
                </div>
              </div>
              <DownloadLink documentId={d.id} />
            </li>
          ))}
        </ul>
      )}
    </Dialog>
  );
}

/**
 * Inline link that resolves the document's presigned GET URL via
 * /documents/:id/download (returns JSON, not a stream) and opens it in a
 * new tab. Plain `<a href>` won't work — the endpoint's response body is
 * `{ url, expiresInSeconds, … }`, not the file bytes.
 */
function DownloadLink({ documentId }: { documentId: Uuid }) {
  const [state, setState] = useState<'idle' | 'loading' | 'error'>('idle');
  async function open() {
    setState('loading');
    try {
      const res = await getDocumentDownloadUrl(documentId);
      window.open(res.url, '_blank', 'noopener');
      setState('idle');
    } catch {
      setState('error');
      setTimeout(() => setState('idle'), 3000);
    }
  }
  return (
    <button
      type="button"
      onClick={open}
      disabled={state === 'loading'}
      className="text-xs text-brand-700 hover:underline disabled:opacity-60"
    >
      {state === 'loading' ? 'Opening…' : state === 'error' ? 'Retry' : 'Download'}
    </button>
  );
}
